// ============================================================
// useVoiceCapture — microphone capture + Gladia STT for the
// Square Code Command Bar voice loop.
//
// Path:
//   1. getUserMedia({ audio: true })  — browser permission prompt
//   2. MediaRecorder records to a Blob (audio/webm; codecs=opus)
//   3. Stop → base64 the bytes
//   4. POST to /api/voice/transcribe (inline mode)
//   5. Server forwards to Gladia and returns the transcript
//
// GLADIA_API_KEY never reaches the browser — the inline payload
// goes to the server route which holds the secret. The microphone
// stream is released as soon as the recording stops.
//
// Errors are surfaced through `error` and never thrown. The
// recorder is best-effort; if MediaRecorder isn't supported the
// hook reports `available: false` and the Command Bar hides the
// mic button gracefully.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api/client'
import { captureHookFailure, recordHookOutcome, recordShellAction } from '../observability/sentry'

type CaptureStatus =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'transcribing'
  | 'error'

export interface VoiceCaptureState {
  available: boolean
  status: CaptureStatus
  error: string | null
  lastTranscript: string | null
}

const PREFERRED_MIME = 'audio/webm;codecs=opus'

function detectAvailability(): boolean {
  if (typeof window === 'undefined') return false
  if (!navigator?.mediaDevices?.getUserMedia) return false
  if (typeof window.MediaRecorder === 'undefined') return false
  return true
}

function pickMime(): string {
  if (typeof window === 'undefined') return 'audio/webm'
  const candidates = [PREFERRED_MIME, 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg']
  for (const mime of candidates) {
    if (
      typeof MediaRecorder !== 'undefined' &&
      typeof MediaRecorder.isTypeSupported === 'function' &&
      MediaRecorder.isTypeSupported(mime)
    ) {
      return mime
    }
  }
  return 'audio/webm'
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  // Chunked to avoid call-stack overflow on large clips.
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return typeof btoa === 'function' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64')
}

export function useVoiceCapture() {
  const [state, setState] = useState<VoiceCaptureState>({
    available: false,
    status: 'idle',
    error: null,
    lastTranscript: null,
  })

  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const stopResolverRef = useRef<((blob: Blob | null) => void) | null>(null)

  useEffect(() => {
    setState((s) => ({ ...s, available: detectAvailability() }))
  }, [])

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop())
      } catch {
        /* ignore */
      }
      streamRef.current = null
    }
  }, [])

  const start = useCallback(async () => {
    if (!detectAvailability()) {
      setState((s) => ({ ...s, available: false, status: 'error', error: 'voice capture unavailable in this browser' }))
      return false
    }
    if (recorderRef.current) return false
    setState((s) => ({ ...s, status: 'requesting', error: null }))
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mime = pickMime()
      const recorder = new MediaRecorder(stream, { mimeType: mime })
      chunksRef.current = []

      recorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      })

      recorder.addEventListener('stop', () => {
        const mimeType = recorder.mimeType || mime || 'audio/webm'
        const blob = chunksRef.current.length
          ? new Blob(chunksRef.current, { type: mimeType })
          : null
        chunksRef.current = []
        releaseStream()
        if (stopResolverRef.current) {
          stopResolverRef.current(blob)
          stopResolverRef.current = null
        }
      })

      recorder.start()
      recorderRef.current = recorder
      setState((s) => ({ ...s, status: 'recording', error: null }))
      recordShellAction('voice.capture_started', { mime })
      return true
    } catch (err) {
      releaseStream()
      const message = (err as Error)?.message || 'microphone permission denied'
      setState((s) => ({ ...s, status: 'error', error: message }))
      captureHookFailure('useCommandDispatch', 'live_error', err, {
        route: 'voice.capture.start',
      })
      return false
    }
  }, [releaseStream])

  /**
   * Stop the current recording, base64 the bytes, send them to
   * /api/voice/transcribe, and return the transcript text. The
   * caller is responsible for handing the transcript to the
   * dispatcher (useCommandDispatch.execute).
   */
  const stopAndTranscribe = useCallback(async (): Promise<string | null> => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') {
      return null
    }
    setState((s) => ({ ...s, status: 'transcribing', error: null }))

    const blob: Blob | null = await new Promise((resolve) => {
      stopResolverRef.current = resolve
      try {
        recorder.stop()
      } catch {
        resolve(null)
      }
    })
    recorderRef.current = null

    if (!blob || blob.size === 0) {
      setState((s) => ({ ...s, status: 'error', error: 'empty audio capture' }))
      return null
    }

    const startedAt = Date.now()
    try {
      const base64 = await blobToBase64(blob)
      const result = await api.voice.transcribeInline({
        audioBase64: base64,
        contentType: blob.type || 'audio/webm',
        filename: 'command-bar.webm',
      })
      const text = (result.transcript?.text || '').trim()
      setState({
        available: true,
        status: text ? 'idle' : 'error',
        error: text ? null : 'gladia returned empty transcript',
        lastTranscript: text || null,
      })
      recordHookOutcome('useCommandDispatch', 'live_success', {
        route: '/api/voice/transcribe',
        durationMs: Date.now() - startedAt,
        text_length: text.length,
      })
      recordShellAction('voice.transcribed', { length: text.length })
      return text || null
    } catch (err) {
      const apiErr = err as ApiError
      const message = apiErr?.message || 'voice_transcribe_failed'
      setState({
        available: true,
        status: 'error',
        error: message,
        lastTranscript: null,
      })
      captureHookFailure('useCommandDispatch', 'live_error', err, {
        route: '/api/voice/transcribe',
        code: apiErr?.code,
        status: apiErr?.status,
      })
      return null
    }
  }, [])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop()
      } catch {
        /* ignore */
      }
    }
    recorderRef.current = null
    chunksRef.current = []
    releaseStream()
    setState((s) => ({ ...s, status: 'idle', error: null }))
  }, [releaseStream])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop()
        } catch {
          /* ignore */
        }
      }
      releaseStream()
    }
  }, [releaseStream])

  return { state, start, stopAndTranscribe, cancel }
}
